import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Shared modal-dialog behaviour: move focus into the dialog on open, trap Tab
// inside it, close on Escape, and restore focus to the trigger on close. Pair
// with role="dialog" aria-modal="true" on the dialog element. Assumes the
// dialog is conditionally mounted (mount = open, unmount = close).
export function useDialog(ref: RefObject<HTMLElement | null>, onClose: () => void) {
  // Keep the latest onClose without re-running the focus effect — otherwise a
  // background re-render (e.g. a roll lands while a modal is open) would yank
  // focus back into the dialog on every parent render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const el = ref.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const items = () =>
      Array.from(el?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(
        (n) => n.offsetParent !== null,
      );

    (items()[0] ?? el)?.focus?.();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const f = items();
      if (f.length === 0) return;
      const first = f[0]!;
      const last = f[f.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previouslyFocused?.focus?.();
    };
    // Mount-only: ref identity is stable; onClose is read via onCloseRef.
  }, [ref]);
}
